import { Box, MenuItem, SelectChangeEvent, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { ImageInput } from 'app/shared/components/image-input/ImageInput';
import { SelectDefault } from 'app/shared/components/select-default/SelectDefault';
import { BoxForm, FormControlDefault, TextFieldForms, TypographyForms } from 'app/shared/layout/register/DefaultFormsComponents';
import { RegisterButton } from 'app/shared/layout/register/RegisterButton';
import { RegisterLayout } from 'app/shared/layout/register/RegisterLayout';
import { RegisterTitle } from 'app/shared/layout/register/RegisterTitle';
import { CategoryVehicleModel } from 'app/shared/model/enumerations/category-vehicle-model.model';
import { SubcategoryVehicleModel } from 'app/shared/model/enumerations/subcategory-vehicle-model';
import { TireFunction } from 'app/shared/model/enumerations/tire-function.model';
import { VehicleTypeEnum, VehicleTypeTireCount } from 'app/shared/model/enumerations/vehicle-type-enum.model';
import React, { useCallback, useEffect, useState } from 'react';
import { translate } from 'react-jhipster';
import { RouteComponentProps } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createEntity, getEntity, reset, updateEntity } from './vehicle-model.reducer';
import VehicleModelTireFunctionModal from './vehicle-model-tire-function-modal';
import TireChassisStructure from 'app/shared/components/tire-chassis-structure/tire-chassis-structure';

export const VehicleModelUpdate = (props: RouteComponentProps<{ id: string }>) => {
  const dispatch = useAppDispatch();

  const vehicleModelEntity = useAppSelector(state => state.vehicleModel.entity);
  const loading = useAppSelector(state => state.vehicleModel.loading);
  const [isNew] = useState(!props.match.params || !props.match.params.id);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [tireFunctionModalOpen, setTireFunctionModalOpen] = useState(false);
  const [tireFunction, setTireFunction] = useState<Record<number, TireFunction>>({});

  const [formData, setFormData] = useState<{
    id: number | null;
    name: string;
    category: string;
    subcategory: string;
    type: string;
    description: string;
    image: string;
    imageContentType: string;
    tireFunction: Record<number, TireFunction>;
  }>({
    id: null,
    name: '',
    category: '',
    subcategory: '',
    type: '',
    description: '',
    image: '',
    imageContentType: '',
    tireFunction: {},
  });

  const tireCount = formData.type
    ? VehicleTypeTireCount[formData.type as VehicleTypeEnum]
    : 0;

  const getTireFunctionMessage = useCallback(() => {
    const configuredCount = Object.keys(formData.tireFunction ?? {}).length;

    if (!tireCount) {
      return '';
    }

    if (!configuredCount) {
      return 'Nenhuma função configurada';
    }

    return `${configuredCount} de ${tireCount} ${
      tireCount === 1 ? 'pneu configurado' : 'pneus configurados'
    }`;
  }, [formData.tireFunction, tireCount]);

  const getTirePositions = () => {
    return Array.from({ length: tireCount }, (_, index) => ({
      position: index + 1,
      steppe: false,
      selected: true,
    }));
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(props.match.params.id));
    }

    setIsDataLoaded(true);
  }, [dispatch, props.match.params.id, isNew]);

  useEffect(() => {
    if (!isNew && vehicleModelEntity && isDataLoaded) {
      // Extrai o mapa interno do wrapper { map: { ... } }
      const rawTireFunction = vehicleModelEntity.tireFunction as Record<string, any> | undefined;
      const extractedMap = rawTireFunction?.['map'] ?? rawTireFunction ?? {};

      setFormData({
        id: vehicleModelEntity.id ?? null,
        name: vehicleModelEntity.name ?? '',
        category: vehicleModelEntity.category ?? '',
        subcategory: vehicleModelEntity.subcategory ?? '',
        type: vehicleModelEntity.type ?? '',
        description: vehicleModelEntity.description ?? '',
        image: vehicleModelEntity.image ?? '',
        imageContentType: vehicleModelEntity.imageContentType ?? '',
        tireFunction: extractedMap,
      });
    }
  }, [vehicleModelEntity, isNew, isDataLoaded]);

  const handleInputChange = (fieldName, value) => {
    setFormData({ ...formData, [fieldName]: value });
  };

  const handleCategory = (event: SelectChangeEvent) => {
    const selectedCategory = event.target.value;

    setFormData({
      ...formData,
      category: selectedCategory,
      subcategory: '',
    });
  };  

  const handleOpenTireFunctionModal = () => {
    setTireFunction(formData.tireFunction ?? {});
    setTireFunctionModalOpen(true);
  };

  const handleCloseTireFunctionModal = () => {
    setTireFunctionModalOpen(false);
  };

  const handleSaveTireFunction = () => {
    setFormData({
      ...formData,
      tireFunction,
    });

    setTireFunctionModalOpen(false);
  };

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    const type = event.target.value;

    setFormData({
      ...formData,
      type,
      tireFunction: {},
    });

    setTireFunction({});
  };

  const handleClose = () => {
    dispatch(reset());
    props.history.push('/vehicle-model' + props.location.search);
  };

  const saveEntity = values => {
    const entity = {
      ...vehicleModelEntity,
      ...values,
      name: values.name,
      category: values.category,
      subcategory: values.subcategory,
      type: values.type,
      description: values.description,
      image: values.image,
      imageContentType: values.imageContentType,
      // Formata o tireFunction para a estrutura wrapper exigida pelo backend: { map: { ... } }
      tireFunction: {
        map: values.tireFunction ?? {},
      },
      company: values.company,
    };

    if (isNew) {
      dispatch(createEntity(entity)).then(() => {
        handleClose();
      });
    } else {
      dispatch(updateEntity(entity)).then(() => {
        handleClose();
      });
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formData?.name) {
      return toast.error('Insira um nome');
    }

    if (!formData?.category) {
      return toast.error('Selecione uma categoria');
    }

    if (!formData?.subcategory) {
      return toast.error('Selecione uma subcategoria');
    }    

    if (!formData?.type) {
      return toast.error('Selecione um tipo de veículo');
    }

    const tireFunctions = formData.tireFunction ?? {};

    for (let position = 1; position <= tireCount; position++) {
      if (!tireFunctions[position]) {
        return toast.error(`Selecione a função do pneu na posição ${position}`);
      }
    }

    saveEntity(formData);
  };

  const subcategoryOptions = useCallback(() => {
    switch (formData.category) {
      case '':
      case null:
        return (
          <MenuItem value="" key="0">
            <Typography>Selecione uma categoria antes</Typography>
          </MenuItem>
        );
      case CategoryVehicleModel.IMPLEMENT:
        return (
          <MenuItem value={SubcategoryVehicleModel.HEAVY} key={SubcategoryVehicleModel.HEAVY}>
            {translate(`fleetSenseApp.subcategoryVehicleModel.${SubcategoryVehicleModel.HEAVY}`)}
          </MenuItem>
        );
      case CategoryVehicleModel.MOTORCYCLE:
      case CategoryVehicleModel.CAR:
        return (
          <MenuItem value={SubcategoryVehicleModel.LIGHT} key={SubcategoryVehicleModel.LIGHT}>
            {translate(`fleetSenseApp.subcategoryVehicleModel.${SubcategoryVehicleModel.LIGHT}`)}
          </MenuItem>
        );
      default:
        return Object.entries(SubcategoryVehicleModel).map(([key, value]) => (
          <MenuItem value={value} key={key}>
            {translate(`fleetSenseApp.subcategoryVehicleModel.${value}`)}
          </MenuItem>
        ));
    }
  }, [formData.category]);

  return (
    <RegisterLayout>
      <Box
        sx={{
          width: { xs: '95%', sm: '90%', lg: '85%', xl: '75%' },
          margin: '0 auto',
        }}
      >
        <RegisterTitle isNew={isNew} contentKey={'fleetSenseApp.vehicleModel'} />
        <Box>
          {loading && !isDataLoaded ? (
            <Box>
              <Typography>Loading...</Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                gap: 4,
                alignItems: 'flex-start',
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              {/* LADO ESQUERDO */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  width: { xs: '100%', md: '50%' },
                }}
              >
                <BoxForm onSubmit={handleSubmit}>
                  {!isNew ? (
                    <FormControlDefault sx={{ display: 'none' }}>
                      <TypographyForms>
                        {translate('global.field.id')}
                      </TypographyForms>

                      <TextFieldForms
                        value={formData.id}
                        id="vehicle-model-id"
                        name="id"
                        data-cy="id"
                      />
                    </FormControlDefault>
                  ) : null}

                  <ImageInput
                    image={formData.image}
                    imageContentType={formData.imageContentType}
                    handleInputChange={handleInputChange}
                  />

                  <FormControlDefault variant={'outlined'}>
                    <TypographyForms>
                      {translate('fleetSenseApp.vehicleModel.name')}
                    </TypographyForms>

                    <TextFieldForms
                      value={formData.name}
                      id="vehicle-model-name"
                      data-cy="name"
                      type="text"
                      name="name"
                      onChange={e =>
                        handleInputChange('name', e.target.value)
                      }
                    />
                  </FormControlDefault>

                  <FormControlDefault variant={'outlined'}>
                    <TypographyForms>
                      {translate('fleetSenseApp.vehicleModel.category')}
                    </TypographyForms>

                    <SelectDefault
                      value={formData.category}
                      onChange={handleCategory}
                    >
                      {Object.entries(CategoryVehicleModel).map(
                        ([key, value]) => (
                          <MenuItem value={value} key={key}>
                            {translate(
                              `fleetSenseApp.CategoryVehicleModel.${value}`
                            )}
                          </MenuItem>
                        )
                      )}
                    </SelectDefault>
                  </FormControlDefault>

                  <FormControlDefault variant={'outlined'}>
                    <TypographyForms>
                      {translate('fleetSenseApp.vehicleModel.subcategory')}
                    </TypographyForms>

                    <SelectDefault
                      value={formData.subcategory}
                      onChange={e =>
                        handleInputChange(
                          'subcategory',
                          e.target.value
                        )
                      }
                    >
                      {subcategoryOptions()}
                    </SelectDefault>
                  </FormControlDefault>

                  <FormControlDefault variant={'outlined'}>
                    <TypographyForms>
                      {translate('fleetSenseApp.vehicleModel.type')}
                    </TypographyForms>

                    <SelectDefault
                      value={formData.type}
                      onChange={handleTypeChange}
                    >
                      {Object.entries(VehicleTypeEnum).map(([key, value]) => (
                        <MenuItem value={key} key={key}>
                          {translate(`fleetSenseApp.VehicleTypeEnum.${key}`)}
                        </MenuItem>
                      ))}
                    </SelectDefault>
                  </FormControlDefault>

                  <FormControlDefault variant={'outlined'}>
                    <TypographyForms>
                      Função dos pneus
                    </TypographyForms>

                    <TextFieldForms
                      value={getTireFunctionMessage()}
                      id="vehicle-model-tire-function"
                      data-cy="tireFunction"
                      name="tireFunction"
                      placeholder="Escolha o tipo de veículo"
                      InputProps={{
                        readOnly: true,
                      }}
                      disabled={!formData.type}
                      onClick={() => {
                        if (formData.type) {
                          handleOpenTireFunctionModal();
                        }
                      }}
                    />
                  </FormControlDefault>

                  <FormControlDefault variant={'outlined'}>
                    <TypographyForms>
                      {translate(
                        'fleetSenseApp.vehicleModel.description'
                      )}
                    </TypographyForms>

                    <TextFieldForms
                      value={formData.description}
                      id="vehicle-model-description"
                      data-cy="description"
                      type="textarea"
                      name="description"
                      onChange={e =>
                        handleInputChange(
                          'description',
                          e.target.value
                        )
                      }
                    />
                  </FormControlDefault>

                  <RegisterButton />
                </BoxForm>
              </Box>

              {/* LADO DIREITO */}
              <Box
                sx={{
                  width: { xs: '100%', md: '50%' },
                  minHeight: 670,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  boxSizing: 'border-box',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    fontWeight: 600,
                  }}
                >
                  Chassi
                </Typography>

                {formData.type ? (
                  <TireChassisStructure
                    vehicleType={formData.type}
                    tireData={getTirePositions()}
                  />
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <Typography color="text.secondary">
                      Selecione o tipo do veículo para visualizar o chassi.
                    </Typography>
                  </Box>
                )}
              </Box>                 
            </Box>
          )}
        </Box>
      </Box>

      <VehicleModelTireFunctionModal
        open={tireFunctionModalOpen}
        tireCount={tireCount}
        tireFunction={tireFunction}
        setTireFunction={setTireFunction}
        onClose={handleCloseTireFunctionModal}
        onSave={handleSaveTireFunction}
      />
    </RegisterLayout>
  );
};

export default VehicleModelUpdate;